import { GeneralDataFacade } from '@/_store/general/general.facade';
import { OffersFacade } from '@/_store/offers/offers.facade';
import { OfferFilter, Selected } from '@/_store/offers/offers.types';
import { MobileDialogService } from '@/common/mobile-dialog/mobile-dialog.service';
import { ITopSearchConfig } from '@/new-top-search/top-search.config';
import { TopSearchDropdownItemMode } from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.types';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { combineLatest, map } from 'rxjs';
import { TopSearchFilterItemsBase } from '../top-search-filter-items.base';

@Component({
	selector: 'app-top-search-filter-items-vertical',
	templateUrl: './top-search-filter-items-vertical.component.html',
	styleUrls: ['../top-search-filter-items.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopSearchFilterItemsVerticalComponent extends TopSearchFilterItemsBase {
	@Input() showAdvancedHeader = true;

	readonly advancedTotalCount$ = combineLatest([this.offersFacade.selected$, this.offersFacade.filterOptions$]).pipe(
		map(([selected, filterOptions]) => this.advancedFiltersTotalCount(selected, filterOptions)),
	);

	private readonly iconByMode: Record<TopSearchDropdownItemMode, string> = {
		[TopSearchDropdownItemMode.LOCATIONS]: 'fa-solid fa-location-dot',
		[TopSearchDropdownItemMode.DATE_RANGE]: 'fa-regular fa-calendar',
		[TopSearchDropdownItemMode.STOPS]: 'fa-solid fa-plane',
		[TopSearchDropdownItemMode.PARTICIPANTS]: 'fa-solid fa-user-group',
		[TopSearchDropdownItemMode.KINDS]: 'fa-regular fa-map',
		[TopSearchDropdownItemMode.BOARDS]: 'fa-solid fa-utensils',
		[TopSearchDropdownItemMode.CONVENIENCES]: 'fa-solid fa-wand-magic-sparkles',
		[TopSearchDropdownItemMode.NIGHTS]: 'fa-regular fa-moon',
		[TopSearchDropdownItemMode.SEARCH]: 'fa-solid fa-magnifying-glass',
		[TopSearchDropdownItemMode.PRICE]: 'fa-solid fa-tag',
	};

	constructor(
		offersFacade: OffersFacade,
		generalFacade: GeneralDataFacade,
		mobileDialogService: MobileDialogService,
	) {
		super(offersFacade, generalFacade, mobileDialogService);
	}

	getIconClass(item: ITopSearchConfig): string {
		return item.iconClass || this.iconByMode[item.id] || 'fa-regular fa-circle';
	}

	onAdvancedFilterChange(): void {
		this.valueChange = true;
		this.fetchOffersWithDebounce();
	}

	clearAllAdvancedFilters(ev: MouseEvent): void {
		ev.preventDefault();
		ev.stopPropagation();
		this.offersFacade.clearKindsFilter();
		this.offersFacade.clearBoardsFilter();
		this.offersFacade.clearConveniencesFilter();
		this.offersFacade.clearPriceRange();
		this.offersFacade.setDefaultNights();
		this.onAdvancedFilterChange();
	}

	private advancedFiltersTotalCount(selected: Selected, fo: OfferFilter): number {
		let total = selected.kinds.length + selected.boards.length + selected.conveniences.length;

		const price = selected.price as { min: number | null; max: number | null };
		if (price?.min != null || price?.max != null) {
			total += 1;
		}

		const bounds = this.defaultNightsBounds(fo);
		if (bounds && (selected.nights.min !== bounds.min || selected.nights.max !== bounds.max)) {
			total += 1;
		}

		return total;
	}

	private defaultNightsBounds(fo: OfferFilter): { min: number; max: number } | null {
		if (!fo.defaultNights?.length) {
			return null;
		}
		return {
			min: Math.min(...fo.defaultNights),
			max: Math.max(...fo.defaultNights),
		};
	}
}
